import React, { useState, useEffect } from "react";
import ComparisonTableMobile from "../components/ComparisonTableMobile";
import MobileSignupModalFlow from "../components/mobile-flow/MobileSignupModalFlow";
import USNursesInterestForm from "../components/USNursesInterestForm";
import { InfoIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";

// Icon mapping for cards (0-indexed)
const getCardIcon = (index) => {
  if (index === 0) return "/svgs/conversation-first.svg";
  if (index === 1) return "/svgs/personalized-plans.svg";
  if (index === 2) return "/svgs/certified-experts.svg";
  if (index === 3) return "/svgs/all-one.svg";
  if (index === 4) return "/svgs/confidence-guarantee.svg";
  return "";
};

export default function USNursesLandingMobile() {
  const { t, i18n } = useTranslation();
  const [showSignupFlow, setShowSignupFlow] = useState(false);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  // Get current month name based on language
  const getCurrentMonthName = () => {
    const monthNames = {
      en: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      es: [
        "enero",
        "febrero",
        "marzo",
        "abril",
        "mayo",
        "junio",
        "julio",
        "agosto",
        "septiembre",
        "octubre",
        "noviembre",
        "diciembre",
      ],
    };
    const lang = i18n.language.split("-")[0] || "en";
    const currentMonthIndex = new Date().getMonth();
    return (
      monthNames[lang]?.[currentMonthIndex] || monthNames.en[currentMonthIndex]
    );
  };

  const currentMonth = getCurrentMonthName();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showPopup]);

  return (
    <div className="min-h-screen w-full bg-[#ffffff] font-['Urbanist'] text-black">
      {/* Notification Banner */}
      {showBanner && (
        <div className="relative z-10 flex min-h-[66px] w-full flex-row items-center justify-between gap-2 bg-[#FFBF00] px-6 py-4">
          <span className="w-full text-center text-sm font-medium text-black">
            {t("usNurses.banner").replace(/October|octubre/i, currentMonth)}
          </span>
          <button
            className="ml-2 text-xl text-black"
            onClick={() => setShowBanner(false)}
            aria-label="Close"
            style={{ lineHeight: 1 }}
          >
            <X size={22} />
          </button>
        </div>
      )}
      {/* Onboarding Modal Flow */}
      {showSignupFlow && (
        <MobileSignupModalFlow onClose={() => setShowSignupFlow(false)} />
      )}
      {/* Hero Section */}
      <div className="relative flex w-full flex-col items-center rounded-b-[40px] bg-white pb-8 pt-8">
        <img
          src="/svgs/logobammbuu.svg"
          alt="bammbuu logo"
          className="mx-auto mb-6"
        />
        <h1 className="whitespace-pre-line px-4 text-center text-[32px] font-extrabold leading-tight">
          {t("usNurses.hero.titleMobile")}
        </h1>
        <p className="mt-3 whitespace-pre-line px-2 text-center text-[18px] font-medium leading-relaxed text-[#042F0C]">
          {t("usNurses.hero.subtitleMobile")}
        </p>
        <div className="mt-6 flex w-full flex-row items-center justify-center gap-3 px-6">
          <button
            className="min-w-[140px] rounded-[18px] border border-black bg-white px-4 py-2 text-sm font-semibold text-black"
            style={{ width: "fit-content" }}
            onClick={() =>
              window.open(
                "https://calendly.com/bammbuu-languages/info-call-llamada-de-informacion",
                "_blank",
              )
            }
          >
            {t("usNurses.hero.freeConsultation")}
          </button>
          <button
            className="min-w-[140px] rounded-[18px] border border-black bg-[#FFBF00] px-4 py-2 text-sm font-semibold text-black"
            style={{ width: "fit-content" }}
            onClick={() => setShowInterestForm(true)}
          >
            {t("usNurses.hero.imInterested")}
          </button>
        </div>
      </div>

      {/* Video Section */}
      <div className="mt-8 flex w-full flex-col items-center rounded-[18.8px] px-6 py-6">
        <div className="relative w-full overflow-hidden rounded-2xl bg-[#eaeaea]">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/DzqaO_ufVQA"
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              aspectRatio: "16/9",
              display: "block",
              borderRadius: "16px",
              minHeight: 200,
            }}
          ></iframe>
        </div>
        <span className="mt-4 block text-center text-xs text-[#5D5D5D]">
          {t("usNurses.hero.videoCaption")}
        </span>
      </div>

      {/* Why Choose Section */}
      <div className="mt-12 flex w-full flex-col items-center px-[15px]">
        <h2 className="w-full text-center text-[32px] font-bold leading-tight">
          {t("usNurses.why.title1")} {t("usNurses.why.title2")}
        </h2>
        <p className="mt-6 whitespace-pre-line text-center text-base leading-relaxed text-[#3D3D3D]">
          {t("usNurses.why.descriptionMobile")}
        </p>
      </div>

      {/* Features Cards */}
      <div className="mt-10 flex w-full flex-col gap-6 px-6">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex flex-col items-center rounded-[48px] border border-[#14B82C] bg-[#F0FDF1] px-6 py-6 text-center"
          >
            <img
              src={getCardIcon(i)}
              alt={t(`usNurses.cards.card${i + 1}.title`)}
              className="mb-3 h-12 w-12"
            />
            <div className="mb-1 text-xl font-bold">
              {t(`usNurses.cards.card${i + 1}.title`)}
            </div>
            <div className="text-base text-[#3D3D3D]">
              {t(`usNurses.cards.card${i + 1}.desc`)}
            </div>
          </div>
        ))}
      </div>

      {/* What You'll Get Section */}
      <div className="mt-12 flex w-full flex-col items-center px-6">
        <h2 className="mb-6 text-center text-[28px] font-semibold">
          {t("usNurses.whatYouGet.title")}
        </h2>
        <div className="flex w-full flex-col gap-4">
          {[
            "feature1",
            "feature2",
            "feature3",
            "feature4",
            "feature5",
            "feature6",
            "feature7",
            "feature8",
          ].map((featureKey, i) => (
            <div
              key={featureKey}
              className="flex items-center gap-3 rounded-2xl px-4 py-3"
            >
              <img src="/svgs/tick-star.svg" alt="check" className="h-6 w-6" />
              <span className="text-base font-medium text-[#042F0C]">
                {t(`usNurses.features.${featureKey}`)}
              </span>
            </div>
          ))}
        </div>
        {/* Info Box */}
        <div className="mt-6 flex items-start gap-2 rounded-xl bg-[#FFFFEA] p-3 text-xs font-normal text-[#3D3D3D]">
          <InfoIcon className="h-4 w-4 shrink-0 text-[#FFBF00]" />
          <span className="italic">{t("usNurses.moneyBackDisclaimer")}</span>
        </div>
      </div>

      {/* Pricing Card */}
      <div className="mt-12 flex w-full flex-col items-center px-6">
        <div className="w-full overflow-hidden rounded-[48px] border border-[#14B82C] bg-[#E6FDE9] shadow-lg">
          <div className="bg-[#FFBF00] py-3 text-center">
            <span className="text-base font-semibold leading-5">
              {t("usNurses.pricing.header")}
            </span>
          </div>
          <div className="flex flex-col items-center px-4 py-8">
            <div className="flex w-full flex-col items-center">
              <div className="mb-2 whitespace-pre-line text-center text-[40px] font-semibold leading-tight tracking-tight text-black">
                {t("usNurses.pricing.priceTitle")}
              </div>
              <div className="mb-3 text-center text-base font-semibold leading-6 text-black">
                {t("usNurses.pricing.includes")}
              </div>
              <div className="relative mx-auto mb-3 w-[85%] overflow-hidden rounded-2xl bg-[#eaeaea]">
                <iframe
                  width="100%"
                  height="100%"
                  style={{
                    aspectRatio: "16/9",
                    display: "block",
                  }}
                  src="https://www.youtube.com/embed/KRdM6uLQRaA"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="flex flex-col items-center justify-center space-y-2">
                <InfoIcon className="h-6 w-6 text-[#FFBF00]" />
                <p className="text-center text-sm font-normal text-[#3d3d3d]">
                  {t("usNurses.pricing.enrollDescription")}
                </p>
              </div>
            </div>

            <div className="mt-6 w-full space-y-3">
              <button
                className="h-[48px] w-full rounded-[24px] border border-[#042F0C] bg-[#14B82C] py-2 text-base font-semibold text-black"
                onClick={() => setShowInterestForm(true)}
              >
                {t("usNurses.hero.imInterested")}
              </button>
              <button
                className="h-[48px] w-full rounded-[24px] border border-[#042F0C] bg-white py-2 text-base font-semibold text-[#12551E]"
                onClick={() =>
                  window.open(
                    "https://calendly.com/bammbuu-languages/info-call-llamada-de-informacion",
                    "_blank",
                  )
                }
              >
                {t("usNurses.hero.freeConsultation")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table Section */}
      <div className="mt-12 flex w-full flex-col items-center px-2">
        <ComparisonTableMobile
          onEnrollClick={() => setShowInterestForm(true)}
          translationPrefix="usNurses"
        />
      </div>

      {/* Footer */}
      <footer className="mt-16 flex w-full flex-col items-center">
        <div className="footer-background-mobile mt-40 flex w-full flex-col space-y-20 rounded-t-[50px] border-2 border-[#B9F9C2] pb-12 pt-24">
          <div className="flex flex-row items-center justify-center">
            <img src="/svgs/logobammbuu.svg" alt="bammbuu logo" className="" />
          </div>
          <div className="flex flex-row items-center justify-center gap-6">
            <a
              href="https://www.instagram.com/bammbuu_languages/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                alt="Instagram"
                src="/svgs/instagram.svg"
                className="h-6 w-6"
              />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61565466403338"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                alt="Facebook"
                src="/svgs/facebook.svg"
                className="h-6 w-6"
              />
            </a>
            <a
              href="https://www.youtube.com/@bammbuu-languages"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img alt="YouTube" src="/svgs/youtube.svg" className="h-6 w-6" />
            </a>
          </div>
          <div className="mb-2 text-center text-sm text-[#6D6D6D]">
            © 2024 All rights reserved
          </div>
          <div className="flex flex-row items-center justify-center gap-6 text-sm text-[#6D6D6D]">
            <span className="underline">Terms & Conditions</span>
            <span className="underline">Privacy Policy</span>
          </div>
        </div>
      </footer>

      {/* US Nurses Interest Form */}
      <USNursesInterestForm
        isOpen={showInterestForm}
        onClose={() => setShowInterestForm(false)}
      />

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="relative flex min-h-[421px] w-[90vw] max-w-[370px] flex-col items-center overflow-hidden rounded-[32px] bg-white p-0 shadow-2xl">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute right-4 top-4 flex items-center justify-center rounded-full bg-[#E7E7E7] bg-opacity-70 p-2 text-[#3D3D3D] hover:bg-gray-100"
              aria-label="Close"
              type="button"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src="/svgs/exam-prep-modal-icon.svg"
              alt={t("usNurses.modal.bannerIconAlt")}
              className="mx-auto mb-6 mt-20"
            />
            <h2 className="mb-3 text-center text-[28px] font-bold text-black">
              {t("usNurses.modal.title")}
            </h2>
            <p className="mb-8 max-w-xs text-center text-base font-medium text-[#454545]">
              {t("usNurses.modal.spotsFilling").replace(
                /October|octubre/i,
                currentMonth,
              )}
            </p>
            <button
              className="mb-6 w-[90%] rounded-full border border-[#042F0C] bg-[#14B82C] py-3 text-base font-medium text-black transition hover:bg-green-700"
              onClick={() => {
                setShowPopup(false);
                setShowInterestForm(true);
              }}
            >
              {t("usNurses.hero.imInterested")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
